import CryptoJS from 'crypto-js'

/**
 * HMAC-SHA256 签名
 * @param content - 待签名的内容
 * @param secret - 密钥
 * @returns 返回 Base64 编码的签名
 */
function signHmacSHA256(content: string, secret: string): string {
  const hash = CryptoJS.HmacSHA256(content, secret)
  return CryptoJS.enc.Base64.stringify(hash)
}

/**
 * AES-256-GCM 加密工具类
 * 使用 Web Crypto API 实现
 */
class AesGcmUtil {
  /**
   * 检查 Web Crypto API 是否可用
   */
  static isSupported(): boolean {
    return typeof crypto !== 'undefined' &&
           typeof crypto.subtle !== 'undefined' &&
           typeof crypto.subtle.encrypt === 'function'
  }
  /**
   * 将 Base64 字符串转换为 ArrayBuffer
   */
  private static base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes.buffer
  }

  /**
   * 将 ArrayBuffer 转换为 Base64 字符串
   */
  private static arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
  }

  /**
   * 导入密钥
   */
  private static async importKey(keyBase64: string): Promise<CryptoKey> {
    const keyBuffer = this.base64ToArrayBuffer(keyBase64)
    return await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    )
  }

  /**
   * AES-256-GCM 加密
   * @param plaintext - 明文数据
   * @param keyBase64 - Base64 编码的密钥
   * @returns Base64 编码的加密数据（格式：IV(12字节) + 密文 + 认证标签(16字节)）
   */
  static async encrypt(plaintext: string, keyBase64: string): Promise<string> {
    // 检查环境支持
    if (!this.isSupported()) {
      console.warn('[Crypto] Web Crypto API 不可用，跳过加密')
    }

    try {
      // 导入密钥
      const key = await this.importKey(keyBase64)

      // 生成随机 IV（12字节）
      const iv = crypto.getRandomValues(new Uint8Array(12))

      // 将明文转换为 ArrayBuffer
      const plaintextBuffer = new TextEncoder().encode(plaintext)

      // 加密
      const encryptedBuffer = await crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv,
          tagLength: 128, // 128位 = 16字节
        },
        key,
        plaintextBuffer
      )

      // 组合 IV + 密文（IV在前，密文在后）
      const result = new Uint8Array(iv.length + encryptedBuffer.byteLength)
      result.set(iv, 0)
      result.set(new Uint8Array(encryptedBuffer), iv.length)

      // Base64 编码返回
      return this.arrayBufferToBase64(result.buffer)
    } catch (error) {
      console.error('AES-256-GCM 加密失败:', error)
      throw new Error(`AES-256-GCM 加密失败: ${(error as Error).message}`)
    }
  }

  /**
   * AES-256-GCM 解密
   * @param encryptedDataBase64 - Base64 编码的加密数据
   * @param keyBase64 - Base64 编码的密钥
   * @returns 解密后的明文数据
   */
  static async decrypt(encryptedDataBase64: string, keyBase64: string): Promise<string> {
    // 检查环境支持
    if (!this.isSupported()) {
      console.warn('[Crypto] Web Crypto API 不可用，跳过解密')
    }

    try {
      // 导入密钥
      const key = await this.importKey(keyBase64)

      // 解码加密数据
      const encryptedBuffer = this.base64ToArrayBuffer(encryptedDataBase64)
      const encryptedArray = new Uint8Array(encryptedBuffer)

      // 提取 IV（前12字节）
      const iv = encryptedArray.slice(0, 12)

      // 提取密文（剩余部分）
      const ciphertext = encryptedArray.slice(12)

      // 解密
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv,
          tagLength: 128, // 128位 = 16字节
        },
        key,
        ciphertext
      )

      // 转换为字符串返回
      return new TextDecoder().decode(decryptedBuffer)
    } catch (error) {
      console.error('AES-256-GCM 解密失败:', error)
      throw new Error(`AES-256-GCM 解密失败: ${(error as Error).message}`)
    }
  }
}

export { signHmacSHA256, AesGcmUtil }
